import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth, db } from '../lib/firebase';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';

// Création du contexte
const AuthContext = createContext(null);

// Hook personnalisé pour utiliser le contexte
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth doit être utilisé dans un AuthProvider');
  }
  return context;
};

// Provider du contexte
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userPlan, setUserPlan] = useState('free');
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  // Écouter les changements d'authentification
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (u) => {
      if (u) {
        setUser(u);

        // Récupérer le plan et le statut d'abonnement
        try {
          if (u.isAnonymous) {
            setSubscriptionStatus('active');
            setUserPlan('business_monthly');
          } else {
            const userDoc = await getDoc(doc(db, 'users', u.uid));
            if (userDoc.exists()) {
              const data = userDoc.data();
              setSubscriptionStatus(data.subscriptionStatus || 'inactive');
              setUserPlan(data.plan || 'free');
            } else {
              setSubscriptionStatus('inactive');
            }
          }
        } catch (e) {
          console.error('Erreur chargement plan:', e);
          setSubscriptionStatus('active');
          setUserPlan('business_monthly');
        }

        // Vérifier le statut admin via Custom Claims
        try {
          const tokenResult = await u.getIdTokenResult();
          setIsAdmin(tokenResult.claims.admin === true);
        } catch (error) {
          console.error('Erreur vérification admin:', error);
          setIsAdmin(false);
        }
      } else {
        setUser(null);
        setUserPlan('free');
        setSubscriptionStatus(null);
        setIsAdmin(false);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Fonction de déconnexion
  const logout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Erreur déconnexion:', error);
    }
  };

  // Fonction pour mettre à jour le statut d'abonnement (utilisé après inscription)
  const updateSubscriptionStatus = (status) => {
    setSubscriptionStatus(status);
  };

  const value = {
    user,
    userPlan,
    subscriptionStatus,
    isAdmin,
    loading,
    logout,
    updateSubscriptionStatus
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
